// apps/web/src/features/russia-requests/components/editor/useRussiaDraftPositions.ts
//
// Local position-row state + per-item REST sync for the Russia SIMPLE
// editor (#1545). Positions persist individually against the
// editor's saved DRAFT request id: add → local row, first named edit →
// POST item, field edit → debounced
// PATCH item, remove → DELETE item, photo add → presign + PUT + PATCH
// item.photos (append, cap REQUEST_ITEM_PHOTOS_MAX). The China aggregate
// draft-save model is NOT reused — Russia items have their own endpoints.
//
// Pure row model + body builders live in `russiaPositions.model.ts`; photo
// add/remove + upload tracking live in `useRussiaPositionPhotos.ts`. This file
// is the orchestrator: row state, create/patch lifecycle, and the assembled API.
//
// @aiInvariant Every row keeps a stable `localId` for React keys; the
//   server UUID lands in `serverId` only after the create POST resolves.
//   Field edits before `serverId` exists are coalesced and flushed once
//   the create settles, so typing during the create round-trip is never
//   lost.
// @aiInvariant `photos` is capped at REQUEST_ITEM_PHOTOS_MAX client-side
//   before the presign call so a stale render cannot push a key past the
//   cap (the wire schema rejects it too).

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  createRussiaRequestItem,
  deleteRussiaRequestItem,
  patchRussiaRequestItem,
} from '../../../../api/russia-requests/items.js';
import {
  PATCH_DEBOUNCE_MS,
  buildPatchBody,
  createInputFromFields,
  emptyRow,
  type RussiaPositionFields,
  type RussiaPositionRow,
} from './russiaPositions.model.js';
import { useRussiaPositionPhotos } from './useRussiaPositionPhotos.js';

// Re-export the public row types so existing consumers keep importing them
//   from this module (the hook is their public entry point).
export type { RussiaPositionFields, RussiaPositionRow };

export interface UseRussiaDraftPositionsOptions {
  /** Saved DRAFT id, or null before the first persist. */
  persistedId: string | null;
  /**
   * Ensure a persisted DRAFT request exists, returning its id (or null if
   * the form is not yet valid enough to create one). Called before the
   * first position POST.
   */
  ensurePersisted: () => Promise<string | null>;
  /** Surfaced as a non-blocking toast on a sync error. */
  onError?: ((message: string) => void) | undefined;
  /**
   * Already-persisted rows to seed in edit mode (#1571). Each carries its
   * `serverId` and existing `photos` so the row state is hydrated WITHOUT
   * routing those photo keys through the upload path.
   *
   * @aiInvariant Seeded rows' photo keys land ONLY in row state — never in the
   *   `uploadedKeysRef` never-saved accumulator. `useState(initialRows ?? [])`
   *   is the guarantee: `addPhoto` is the sole writer of `uploadedKeysRef`, and
   *   seeding bypasses it entirely, so a confirmed discard-close never GCs a
   *   real saved photo (#1561).
   */
  initialRows?: RussiaPositionRow[] | undefined;
}

export interface RussiaDraftPositionsApi {
  rows: RussiaPositionRow[];
  /** Append a fresh empty row and POST it once a persisted id exists. */
  addPosition: () => void;
  patchPosition: (localId: string, patch: Partial<RussiaPositionFields>) => void;
  removePosition: (localId: string) => void;
  addPhoto: (localId: string, file: File) => Promise<{ objectKey: string } | null>;
  removePhoto: (localId: string, key: string) => void;
  /** Persist all create-valid local rows against a known parent id. */
  flushPending: (parentId: string) => Promise<void>;
  /** Await any in-flight photo upload, then cancel every pending debounced
   *   PATCH so no orphaned write can re-reference a just-cleaned-up key after
   *   navigation, and return the full session accumulator of object keys whose
   *   storage PUT settled, so a confirmed discard-close can GC the orphaned
   *   storage objects. The accumulator survives photo/row removal and includes
   *   keys whose upload settles after discard begins. Also marks the session
   *   discarded so a still-in-flight CREATE that resolves afterward suppresses
   *   its post-create replay PATCH — otherwise it would re-reference a key the
   *   cleanup endpoint just deleted (COD-REV-ROUND3-001). Also awaits any
   *   already-fired in-flight position PATCH so its committed photo reference is
   *   visible to the server's not-referenced gate before cleanup runs — an
   *   already-sent PATCH cannot be canceled (COD-REV-ROUND4-001). Over-inclusion
   *   is safe: the server's not-referenced gate preserves any saved key (#1561). */
  collectNeverSavedPhotoKeys: () => Promise<string[]>;
  /** True while any row holds unpersisted work (no serverId yet, or a photo
   *   upload in flight) — lets the host include positions in the drawer's
   *   unsaved-changes guard so closing does not silently drop them
   *   (#1545 round-6 COD-REV-001). */
  hasUnsavedWork: boolean;
}

export function useRussiaDraftPositions(
  opts: UseRussiaDraftPositionsOptions,
): RussiaDraftPositionsApi {
  // Seed already-persisted rows (edit mode) only on the first render — the
  //   lazy initializer keeps a later prop-identity change from clobbering live
  //   edits, and crucially seeds photos into row state WITHOUT the upload path
  //   so existing keys never enter the never-saved accumulator (#1571/#1561).
  const [rows, setRows] = useState<RussiaPositionRow[]>(() => opts.initialRows ?? []);
  const rowsRef = useRef<RussiaPositionRow[]>(rows);
  rowsRef.current = rows;

  const ensurePersistedRef = useRef(opts.ensurePersisted);
  ensurePersistedRef.current = opts.ensurePersisted;
  const onErrorRef = useRef(opts.onError);
  onErrorRef.current = opts.onError;

  // Per-row debounce timers for PATCH coalescing.
  const patchTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  // In-flight position PATCH promises (already-fired debounce flushes). A
  //   confirmed discard awaits these BEFORE cleanup so the server's
  //   not-referenced gate sees their committed photo references and preserves
  //   those keys — an already-sent PATCH cannot be canceled (COD-REV-ROUND4-001).
  const patchInFlightRef = useRef<Set<Promise<void>>>(new Set());
  // Rows mid-create (no serverId yet) whose edits must replay once the
  // POST resolves. Keyed by localId → the fields to PATCH after create.
  const pendingAfterCreateRef = useRef<Set<string>>(new Set());
  // Per-row in-flight create promise (localId → resolves to the new
  //   serverId). The single create lock: a concurrent flush joins the SAME
  //   create and observes its outcome, so there is no duplicate POST and no
  //   skipped row that falsely reports success on a failed create (#1545
  //   round-3 F-002).
  const createInFlightRef = useRef<Map<string, Promise<string>>>(new Map());
  // Rows removed while their create POST was still in flight: there was no
  //   serverId to DELETE yet, so the create's completion must delete the
  //   orphan item it produces (#1545 round-5 COD-001).
  const removedInFlightRef = useRef<Set<string>>(new Set());
  // Set once a confirmed discard-close begins collecting cleanup keys. Guards
  //   any write that a still-in-flight CREATE would otherwise schedule AFTER
  //   the cleanup endpoint has already deleted the photo object, which would
  //   leave a saved item referencing a deleted key (COD-REV-ROUND3-001).
  const discardedRef = useRef(false);

  const reportError = useCallback((message: string) => {
    onErrorRef.current?.(message);
  }, []);

  const mutateRow = useCallback(
    (localId: string, fn: (row: RussiaPositionRow) => RussiaPositionRow) => {
      setRows((prev) => prev.map((r) => (r.localId === localId ? fn(r) : r)));
    },
    [],
  );

  const findRow = useCallback(
    (localId: string): RussiaPositionRow | undefined =>
      rowsRef.current.find((r) => r.localId === localId),
    [],
  );

  // Per-row PATCH coalescing. Read the flush through a ref so the timer
  //   always runs the latest implementation while keeping a stable
  //   `schedulePatch` identity.
  const flushPatchRef = useRef<(localId: string) => void>(() => undefined);
  const schedulePatch = useCallback((localId: string) => {
    const timers = patchTimersRef.current;
    const existing = timers.get(localId);
    if (existing) clearTimeout(existing);
    timers.set(
      localId,
      setTimeout(() => {
        timers.delete(localId);
        void flushPatchRef.current(localId);
      }, PATCH_DEBOUNCE_MS),
    );
  }, []);

  const cancelAllPatchTimers = useCallback(() => {
    for (const timer of patchTimersRef.current.values()) clearTimeout(timer);
    patchTimersRef.current.clear();
  }, []);

  const awaitInFlightPatches = useCallback(async (): Promise<void> => {
    await Promise.allSettled([...patchInFlightRef.current]);
  }, []);

  const photos = useRussiaPositionPhotos({
    findRow,
    mutateRow,
    schedulePatch,
  });
  const { addPhoto, removePhoto, awaitUpload } = photos;

  // Create one row against an already-known parent id, deduped per localId so
  //   concurrent callers (the persistedId effect + an explicit-Save flush)
  //   share one POST and one outcome. Resolves to the new serverId; rejects if
  //   the create fails so callers can decide whether to surface it.
  const createRow = useCallback(
    (parentId: string, row: RussiaPositionRow): Promise<string> => {
      const existing = createInFlightRef.current.get(row.localId);
      if (existing !== undefined) return existing;
      const run = createRussiaRequestItem(parentId, createInputFromFields(row)).then((created) => {
        // If the row was removed while this create was in flight, the DELETE
        //   could not fire (no serverId existed yet) — reconcile now so the
        //   user's delete is honored and no orphan item persists
        //   (#1545 round-5 COD-001).
        if (removedInFlightRef.current.delete(row.localId)) {
          deleteRussiaRequestItem(parentId, created.id).catch((err: unknown) => {
            reportError(err instanceof Error ? err.message : String(err));
          });
          return created.id;
        }
        mutateRow(row.localId, (r) => ({ ...r, serverId: created.id }));
        return created.id;
      });
      createInFlightRef.current.set(row.localId, run);
      // Clean up the lock on settle without creating an unhandled rejection —
      //   the original `run` is what callers await and handle.
      run
        .catch(() => undefined)
        .finally(() => {
          createInFlightRef.current.delete(row.localId);
        });
      return run;
    },
    [mutateRow, reportError],
  );

  const createOnServer = useCallback(
    // `seed` carries the row's localId; the live row is re-read after the
    //   parent-materialization await so the create reflects current fields.
    //   Background (incremental / autosave) create: errors are reported and
    //   swallowed because there is no navigation to block here.
    async (seed: RussiaPositionRow) => {
      const localId = seed.localId;
      if (discardedRef.current) return;
      if (createInFlightRef.current.has(localId)) return;
      let id: string | null = null;
      try {
        id = await ensurePersistedRef.current();
      } catch {
        return;
      }
      if (id === null) return;
      // Re-read after the await: the row may have been removed.
      const live = findRow(localId);
      if (!live || live.serverId !== null) return;
      try {
        await createRow(id, live);
        // Replay any edit / photo append that landed while creating. The
        //   create body never carries photos, so reconcile them here too
        //   whenever the settled row holds any — covering a photo+name that
        //   rode along in the same single flush that triggered the create.
        const settled = findRow(localId);
        const hadPending = pendingAfterCreateRef.current.delete(localId);
        // Suppress the post-create photo/edit replay on a confirmed discard: the
        //   photo key is being cleaned up, so a late PATCH would re-reference a
        //   deleted storage object (COD-REV-ROUND3-001). The item persists with
        //   no photo — consistent with the persisted-row discard behavior.
        if (
          !discardedRef.current &&
          (hadPending || (settled !== undefined && settled.photos.length > 0))
        ) {
          schedulePatch(localId);
        }
      } catch (err) {
        reportError(err instanceof Error ? err.message : String(err));
      }
    },
    [findRow, createRow, reportError, schedulePatch],
  );

  // Flush the current row fields/photos to the server. Builds a patch from
  // the live row so coalesced edits all land in one PATCH.
  const flushPatch = useCallback(
    async (localId: string) => {
      const row = findRow(localId);
      if (!row) return;
      if (row.serverId === null) {
        // Not persisted yet. The create body requires a non-empty name
        //   (wire schema), so POST only once the row is create-valid;
        //   otherwise defer until a later edit supplies the name. If a
        //   create is already in flight, mark to replay after it settles.
        if (!createInFlightRef.current.has(localId) && row.name.trim().length > 0) {
          await createOnServer(row);
        } else {
          pendingAfterCreateRef.current.add(localId);
        }
        return;
      }
      // Register the network promise synchronously BEFORE any await so a discard
      //   collector that runs right after the timer fires observes it in flight
      //   and orders cleanup after its committed photo reference (ROUND4-001).
      const p = patchRussiaRequestItem(
        opts.persistedId ?? row.serverId,
        row.serverId,
        buildPatchBody(row),
      )
        .then(() => undefined)
        .catch((err: unknown) => {
          reportError(err instanceof Error ? err.message : String(err));
        });
      patchInFlightRef.current.add(p);
      void p.finally(() => {
        patchInFlightRef.current.delete(p);
      });
      await p;
    },
    [findRow, createOnServer, opts.persistedId, reportError],
  );

  flushPatchRef.current = flushPatch;

  const addPosition = useCallback(() => {
    // Append a local row only. An empty position is not create-valid (the
    //   wire schema requires a non-empty name), so the POST is deferred to
    //   the first flush once a name is typed — see flushPatch.
    setRows((prev) => [...prev, emptyRow(crypto.randomUUID())]);
  }, []);

  const patchPosition = useCallback(
    (localId: string, patch: Partial<RussiaPositionFields>) => {
      mutateRow(localId, (r) => ({ ...r, ...patch }));
      schedulePatch(localId);
    },
    [mutateRow, schedulePatch],
  );

  const removePosition = useCallback(
    (localId: string) => {
      const timer = patchTimersRef.current.get(localId);
      if (timer) {
        clearTimeout(timer);
        patchTimersRef.current.delete(localId);
      }
      pendingAfterCreateRef.current.delete(localId);
      const row = findRow(localId);
      setRows((prev) => prev.filter((r) => r.localId !== localId));
      const serverId = row?.serverId ?? null;
      const reqId = opts.persistedId;
      if (serverId !== null && reqId !== null) {
        deleteRussiaRequestItem(reqId, serverId).catch((err: unknown) => {
          reportError(err instanceof Error ? err.message : String(err));
        });
      } else if (createInFlightRef.current.has(localId)) {
        // Create POST still in flight: no serverId to DELETE yet. Tombstone so
        //   createRow deletes the item the moment the POST resolves (COD-001).
        removedInFlightRef.current.add(localId);
      }
    },
    [findRow, opts.persistedId, reportError],
  );

  // Persist every create-valid local row against an already-known parent id,
  //   and flush pending debounced patches for already-created rows. The host
  //   calls this on explicit Save (before navigating away) so positions typed
  //   while the header was still invalid are not silently dropped (#1545
  //   F-003). Uses the passed `parentId` directly — `opts.persistedId` may not
  //   have propagated yet when Save just created the parent.
  const flushPending = useCallback(
    async (parentId: string): Promise<void> => {
      const results = await Promise.allSettled(
        rowsRef.current.map(async (snapshot) => {
          const localId = snapshot.localId;
          // Wait for any in-flight photo upload so its key is part of the
          //   final write, then re-read the live row (#1545 round-6
          //   COD-REV-002). A row removed during the wait is skipped.
          const upload = awaitUpload(localId);
          if (upload) await upload;
          const row = findRow(localId);
          if (!row) return;
          if (row.serverId === null) {
            if (row.name.trim().length === 0) return; // not create-valid
            // Join any in-flight create (e.g. the persistedId effect) so we
            //   observe its outcome instead of skipping and falsely reporting
            //   success; otherwise this starts the create. A JOINED create may
            //   carry a stale scalar snapshot — a background create can have
            //   started before the user's later edits (#1545 round-4 F-001).
            const joinedInFlight = createInFlightRef.current.has(row.localId);
            const serverId = await createRow(parentId, row);
            // Save owns the final write: cancel any pending debounce so a stale
            //   coalesced PATCH cannot fire (and setState) after navigation.
            const timer = patchTimersRef.current.get(row.localId);
            if (timer) {
              clearTimeout(timer);
              patchTimersRef.current.delete(row.localId);
            }
            const hadPending = pendingAfterCreateRef.current.delete(row.localId);
            // The create body never carries photos; a joined create may also
            //   miss the latest scalar edits. Flush the full latest body when we
            //   joined a pre-existing create, replayed a pending edit, or have
            //   photos to land — so Save persists exactly what the user typed.
            if (joinedInFlight || hadPending || row.photos.length > 0) {
              await patchRussiaRequestItem(parentId, serverId, buildPatchBody(row));
            }
            return;
          }
          // Already created — flush any pending debounced patch now so the
          //   latest edits land before the host navigates away.
          const timer = patchTimersRef.current.get(row.localId);
          if (timer) {
            clearTimeout(timer);
            patchTimersRef.current.delete(row.localId);
          }
          await patchRussiaRequestItem(parentId, row.serverId, buildPatchBody(row));
        }),
      );
      // Fail closed (#1545 round-3 F-002): every row is still attempted
      //   (allSettled) so successful writes persist, but if any failed, throw
      //   so the host keeps the editor open instead of navigating away on a
      //   partial Save. The user can re-Save to retry the rest.
      const rejected = results.find((r) => r.status === 'rejected');
      if (rejected !== undefined && rejected.status === 'rejected') {
        throw rejected.reason instanceof Error
          ? rejected.reason
          : new Error(String(rejected.reason));
      }
    },
    [createRow, findRow, awaitUpload],
  );

  // When the parent materializes (via autosave or explicit Save), flush any
  //   create-valid local rows so positions entered before the parent existed
  //   persist without needing a further edit (#1545 F-003). Idempotent: the
  //   per-row create lock + serverId guard prevent a double create.
  useEffect(() => {
    if (opts.persistedId === null) return;
    for (const row of rowsRef.current) {
      if (
        row.serverId === null &&
        row.name.trim().length > 0 &&
        !createInFlightRef.current.has(row.localId)
      ) {
        void createOnServer(row);
      }
    }
  }, [opts.persistedId, createOnServer]);

  const hasUnsavedWork = rows.some((r) => r.serverId === null || r.uploading);

  // Discard-close: let any in-flight upload settle (it pins its key + schedules
  //   a PATCH), THEN cancel every pending debounced PATCH. Without this an
  //   orphaned timer could write a just-cleaned-up key back into
  //   RussiaRequestItem.photos AFTER navigation, leaving a saved position with
  //   a broken photo reference (COD-REV-ROUND2-001). With no pending write,
  //   shipping the full accumulator stays safe: the server's not-referenced
  //   gate preserves any already-saved key and deletes the genuine orphans.
  // Order matters: awaitAllUploads() BEFORE cancelAllPatchTimers() so a timer
  //   scheduled by an upload that settles during the await is also canceled.
  const collectNeverSavedPhotoKeys = useCallback(async (): Promise<string[]> => {
    // Set BEFORE awaiting uploads so a CREATE that resolves during/after the
    //   await sees the discard and suppresses its post-create replay PATCH
    //   (COD-REV-ROUND3-001).
    discardedRef.current = true;
    await photos.awaitAllUploads();
    cancelAllPatchTimers();
    // An already-fired debounce PATCH cannot be canceled; await it so its photo
    //   reference is committed before cleanup runs and the server's
    //   not-referenced gate preserves that key (COD-REV-ROUND4-001).
    await awaitInFlightPatches();
    return photos.collectUploadedKeys();
  }, [photos, cancelAllPatchTimers, awaitInFlightPatches]);

  return {
    rows,
    addPosition,
    patchPosition,
    removePosition,
    addPhoto,
    removePhoto,
    flushPending,
    hasUnsavedWork,
    collectNeverSavedPhotoKeys,
  };
}
